/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosError } from 'axios';
import { ErrorHandlerActions } from 'src/core/types/ErrorHandlerActions';

/**
 * Represents a chain of error handlers that when executed,
 * will keep on calling handlers on till one of them succeeds.
 */
export const createHandlersManager = () => {
    let _head: ErrorHandlerActions;
    let _tail: ErrorHandlerActions;

    return {
        /**
         * Adds a new handler to the pipeline of handlers
         * to execute. Attaches the last handler in the chain
         * (if exists) to the new handler.
         *
         * @param handler The handler to add to the chain.
         */
        add: (handler: ErrorHandlerActions): void => {
            if (!_head) {
                _head = handler;
                _tail = handler;
            } else {
                _tail.setNext(handler);
                _tail = handler;
            }
        },

        /**
         * Handles the error given by passing it to the error
         * handlers responsibility chain.
         *
         * @param error The error to handle.
         * @returns A well formatted error object.
         */
        handle: async (error: AxiosError) => {
            if (_head) {
                return _head.handle(error);
            }

            throw new Error('Pipeline has no handlers.');
        }
    };
};
